import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    model,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    SimpleChanges,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    OrderFormComment,
    OrderFormCommentAction,
    OrderFormCommentActionType,
    OrderFormCommentTagInfo,
    OrderFormCommentTagsInfo
} from '@trade-platform/ui-shared';
import {
    AixButtonComponent,
    AixDataTestingDirective,
    AixExpansionPanelComponent,
    AixModalComponent,
    AixTooltipDirective,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { Subscription } from 'rxjs';
import { commentTag as commentTagTypes, commentType } from '@trade-platform/lib-enums';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../../base.orders.store.facade';
import { FormatPipeModule } from 'ngx-date-fns';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AixOrderFormCommentTagsComponent } from '../order-form-comment-tags/order-form-comment-tags';
import { AixOrderFormCommentsListComponent } from '../order-form-comments-list/order-form-comments-list';

@Component({
    selector: 'aix-order-form-comment',
    templateUrl: 'order-form-comment.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [
        AixModalComponent,
        AixDataTestingDirective,
        NgClass,
        FormsModule,
        AixOrderFormCommentTagsComponent,
        AixButtonComponent,
        AixOrderFormCommentsListComponent,
        AixExpansionPanelComponent,
        FormatPipeModule,
        AixTooltipDirective
    ]
})
export class AixOrderFormCommentComponent implements OnInit, OnChanges, OnDestroy {
    store = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    private ref = inject(ChangeDetectorRef);

    comment = model<OrderFormComment>();
    canReply = input<boolean>(false);

    onCommentAction = output<OrderFormCommentAction>();

    deleteModal = viewChild<AixModalComponent>('deleteModal');

    editModeActive = false;
    replyModeActive = false;
    commentToDelete: OrderFormComment | null = null;

    commentTypes = commentType;

    subscriptions: Subscription[] = [];

    commentReplyLabel = '';
    commentReplyText = '';
    commentChildrenHeaderClass = {
        'order-form-comments__no-border': true,
        'order-form-comments__header': true,
        'u-mt16': true,
        'u-fw600': true,
        'br-primary__color': true
    };
    commentChildrenBodyClass = {
        'order-form-comments__no-border': true
    };
    commentChildrenArrowClass = {
        'br-primary__color': true
    };

    commentText = '';
    commentDate?: Date | null = null;
    commentTag?: string | null = null;
    commentPrivate?: boolean | undefined;
    commentTagTypes = commentTagTypes;
    commentTagInfo: OrderFormCommentTagInfo;
    readonly commentTagsInfo: OrderFormCommentTagsInfo = {
        [commentTagTypes.change]: {
            class: 'warn',
            label: 'Needs Changes'
        },
        [commentTagTypes.clarification]: {
            class: 'alert',
            label: 'Needs Clarification'
        },
        [commentTagTypes.information]: {
            class: 'note',
            label: 'Note'
        }
    };

    isPrivate = false;
    isCommentChildrenClosed = false;

    cancelButtonType = BUTTON_TYPE.link;
    saveButtonType = BUTTON_TYPE.primary;
    replyPrimaryButtonType = BUTTON_TYPE.primary;
    replySecondaryButtonType = BUTTON_TYPE.secondary;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    ngOnInit() {
        const file = this.store.order.files.find(f => f.id === this.comment()?.documentId);
        this.isPrivate = file?.isPrivate === true;

        this.subscriptions.push(
            this.store.actions.updateFormComment.success$.subscribe(action => {
                const updatedComment = action.payload;
                if (updatedComment && updatedComment.id === this.comment()?.id) {
                    this.editModeActive = false;
                    this.comment.set({
                        ...(this.comment() as OrderFormComment),
                        updatedAt: updatedComment.updatedAt
                    });
                }
            }),
            this.store.actions.createFormComment.success$.subscribe(action => {
                const createdComment = action.payload;
                if (createdComment && createdComment.parentId === this.comment()?.id) {
                    this.replyModeActive = false;
                    this.commentReplyText = '';
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.comment && changes.comment.currentValue) {
            if (
                this.comment() &&
                this.comment()?.commentTag &&
                Object.keys(this.commentTagsInfo).includes(this.comment()?.commentTag ?? '')
            ) {
                this.commentDate = this.comment()?.updatedAt
                    ? new Date(this.comment()?.updatedAt ?? '')
                    : undefined;
                this.commentTag = this.comment()?.commentTag;
                this.commentPrivate = this.comment()?.commentPrivate;
                this.commentTagInfo = this.commentTagsInfo[this.comment()?.commentTag ?? ''];
            }

            this.commentText = this.comment()?.text ?? '';

            this.commentReplyLabel = this.getCommentReplyLabel(this.comment() as OrderFormComment);

            const file = this.store.order.files.find(f => f.id === this.comment()?.documentId);
            this.isPrivate = file?.isPrivate === true;
        }
    }

    editComment() {
        this.editModeActive = true;
    }

    onCancelEdit() {
        if (this.comment()) {
            const comment = this.comment() as OrderFormComment;
            this.commentText = comment.text;
            this.commentTag = comment.commentTag;
            this.commentPrivate = comment.commentPrivate;
            this.editModeActive = false;
        }
    }

    onCancelReply() {
        this.commentReplyText = '';
        this.replyModeActive = false;
    }

    onClickDelete(comment: OrderFormComment) {
        this.commentToDelete = comment;
        this.deleteModal()?.openModal();
        this.ref.detectChanges();
    }

    onClickSave(comment: OrderFormComment) {
        comment.text = this.commentText;
        comment.commentTag = this.commentTag as string;
        comment.commentPrivate = this.commentPrivate;
        this.transmitCommentAction('save', comment);
    }

    onClickReply() {
        if (this.replyModeActive && this.comment()) {
            const comment = this.comment() as OrderFormComment;
            this.store.actions.createFormComment.dispatch({
                parentId: comment.id,
                commentType: comment.commentType,
                commentTag: comment.commentTag,
                text: this.commentReplyText.trim(),
                documentName: comment.documentName,
                data: {
                    orderId: comment.orderId,
                    documentId: comment.documentId
                }
            });
        }
        this.replyModeActive = true;
    }

    onCommentPrivateChange(value: boolean) {
        this.commentPrivate = value;
    }

    modalSelected(e: string) {
        switch (e) {
            case 'Yes, continue':
                this.transmitCommentAction('delete', this.commentToDelete as OrderFormComment);
                this.commentToDelete = null;
                break;
        }
    }

    transmitCommentAction(action: OrderFormCommentActionType, comment: OrderFormComment) {
        this.onCommentAction.emit({ type: action, comment: comment });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private getCommentReplyLabel(comment: OrderFormComment): string {
        if (comment.children) {
            const childrenCount = comment.children.length;
            const replyLabel = comment.children.length > 1 ? 'replies' : 'reply';
            return `${childrenCount} ${replyLabel}`;
        }
        return '';
    }
}
